import { openDB } from 'idb';

const DB_NAME = 'NautilusOneDB';
const DB_VERSION = 9;
const STORE_NAME = 'offlineRequests';

export interface OfflineRequest {
  id?: number;
  url: string;
  method: string;
  headers?: Record<string, string>;
  body: string;
  timestamp: number;
  metadata?: {
    mockId?: string;
  };
}

export const initDB = async () => {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, newVersion, transaction) {
      const logs = [];
      logs.push({
        timestamp: Date.now(),
        oldVersion,
        newVersion,
        event: 'upgrade_start'
      });

      try {

      // Recreate messages store to fix keyPath schema mismatch
      if (oldVersion < 8 && db.objectStoreNames.contains('messages')) {
        db.deleteObjectStore('messages');
      }

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains('checklists')) {
        db.createObjectStore('checklists', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('blending_sheets')) {
        db.createObjectStore('blending_sheets', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('jobs')) {
        db.createObjectStore('jobs', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('attendance')) {
        db.createObjectStore('attendance', { keyPath: 'id' }); // key is monthStr
      }
      if (!db.objectStoreNames.contains('messages')) {
        db.createObjectStore('messages', { keyPath: 'Id' }); // Key is Id (serial / mockId)
      }
      if (!db.objectStoreNames.contains('offline_media')) {
        db.createObjectStore('offline_media', { keyPath: 'id' }); // key is offline_uuid / mockId
      }
      if (!db.objectStoreNames.contains('migration_logs')) {
        db.createObjectStore('migration_logs', { keyPath: 'id', autoIncrement: true });
      }

      logs.push({
        timestamp: Date.now(),
        oldVersion,
        newVersion,
        event: 'upgrade_success'
      });

      } catch (err) {
        logs.push({
          timestamp: Date.now(),
          oldVersion,
          newVersion,
          event: 'upgrade_failure',
          error: String(err)
        });
        throw err;
      } finally {
        const logStore = transaction.objectStore('migration_logs');
        for (const log of logs) {
          logStore.put(log);
        }
      }
    },
  });
};

export const enqueueRequest = async (url: string, method: string, headers: Record<string, string>, body: any, metadata?: { mockId?: string }) => {
  const db = await initDB();
  const request: OfflineRequest = {
    url,
    method,
    headers,
    body: JSON.stringify(body),
    timestamp: Date.now(),
    metadata
  };
  await db.add(STORE_NAME, request);
  console.log(`[OfflineSync] Enqueued request for ${url}`, metadata);
};

export const purgeOfflineCache = async () => {
  try {
    const db = await initDB();
    const tx = db.transaction([STORE_NAME, 'checklists', 'jobs', 'attendance', 'messages', 'offline_media'], 'readwrite');
    await tx.objectStore(STORE_NAME).clear();
    await tx.objectStore('checklists').clear();
    await tx.objectStore('jobs').clear();
    await tx.objectStore('attendance').clear();
    await tx.objectStore('messages').clear();
    await tx.objectStore('offline_media').clear();
    await tx.done;
    console.log('[OfflineSync] Offline stores purged successfully on logout.');
  } catch (err) {
    console.error('[OfflineSync] Error purging offline cache:', err);
  }
};

export const processOfflineQueue = async () => {
  if (!navigator.onLine) {
    console.log('[OfflineSync] Device is offline. Waiting to process queue.');
    return;
  }

  const db = await initDB();
  let allRequests = await db.getAll(STORE_NAME) as OfflineRequest[];
  
  if (allRequests.length === 0) return;

  console.log(`[OfflineSync] Processing ${allRequests.length} queued offline requests...`);

  const mockIdToRealIdMap: Record<string, string> = {};

  for (const req of allRequests) {
    try {
      let finalUrl = req.url;
      let finalBody = req.body;

      // Translate IDs for any mocked IDs mapped from previous POSTs
      Object.keys(mockIdToRealIdMap).forEach((mockId) => {
        const realId = mockIdToRealIdMap[mockId];
        if (finalUrl.includes(mockId)) {
          finalUrl = finalUrl.replace(mockId, realId);
        }
        if (finalBody.includes(mockId)) {
          finalBody = finalBody.replaceAll(mockId, realId);
        }
      });

      const parsedBody = JSON.parse(finalBody);

      // Offline Media Processing: Check if there's a cached offline attachment matching this payload
      if (parsedBody.attachment && parsedBody.attachment.offline_uuid) {
        const uuid = parsedBody.attachment.offline_uuid;
        const cachedMedia = await db.get('offline_media', uuid);
        
        if (cachedMedia && cachedMedia.blob) {
          console.log(`[OfflineSync] Replaying media upload for ${cachedMedia.name}...`);
          
          const formData = new FormData();
          formData.append("file", cachedMedia.blob, cachedMedia.name);
          formData.append("category", "Messaging Attachment");

          const mediaRes = await fetch("/api/upload", {
            method: "POST",
            body: formData
          });

          if (mediaRes.ok) {
            const mediaData = await mediaRes.json();
            // Replace local attachment metadata with real server URL
            parsedBody.attachment = {
              url: mediaData.url,
              type: cachedMedia.type || "image",
              name: cachedMedia.name
            };
            finalBody = JSON.stringify(parsedBody);
            console.log(`[OfflineSync] Media uploaded successfully. URL: ${mediaData.url}`);
            
            // Delete media from cache to free up space
            await db.delete('offline_media', uuid);
          } else {
            console.error(`[OfflineSync] Media upload failed. Skipping message sync.`);
            continue;
          }
        }
      }

      const response = await fetch(finalUrl, {
        method: req.method,
        headers: req.headers || { "Content-Type": "application/json" },
        body: finalBody
      });

      if (response.ok) {
        const responseData = await response.json();
        console.log(`[OfflineSync] Successfully replayed request to ${finalUrl}`);
        
        // If it was a POST creation request, save the returned real ID map
        if (req.method === 'POST' && req.metadata?.mockId) {
          const mockId = req.metadata.mockId;
          const realId = responseData.id || responseData.message?.Id;
          if (realId) {
            mockIdToRealIdMap[mockId] = realId;
            console.log(`[OfflineSync] Mapped mock ID ${mockId} to real ID ${realId}`);
            
            // Check if it's a checklist or message creation response
            if (finalUrl.includes("/api/messages")) {
              const msgTx = db.transaction('messages', 'readwrite');
              const msgStore = msgTx.objectStore('messages');
              await msgStore.delete(mockId);
              await msgStore.put(responseData.message || responseData);
              await msgTx.done;
            } else {
              const checklistTx = db.transaction('checklists', 'readwrite');
              const checklistStore = checklistTx.objectStore('checklists');
              await checklistStore.delete(mockId);
              await checklistStore.put(responseData);
              await checklistTx.done;
            }
          }
        }

        await db.delete(STORE_NAME, req.id!);
      } else {
        console.error(`[OfflineSync] Replay failed with status ${response.status} for ${finalUrl}`);
        
        // Handle Membership Suspension & Unauthorized Access Conflicts
        if (response.status === 403) {
          const errData = await response.json().catch(() => null);
          const errMsg = errData?.error || "";
          
          if (errMsg === "Membership Suspended") {
            console.warn("[OfflineSync] Membership is suspended! Evicting cache and forcing logout.");
            const tx = db.transaction([STORE_NAME, 'checklists', 'jobs', 'attendance', 'messages', 'offline_media'], 'readwrite');
            await tx.objectStore(STORE_NAME).clear();
            await tx.objectStore('checklists').clear();
            await tx.objectStore('jobs').clear();
            await tx.objectStore('attendance').clear();
            await tx.objectStore('messages').clear();
            await tx.objectStore('offline_media').clear();
            await tx.done;

            if (typeof window !== "undefined") {
              window.location.href = "/auth/login?error=suspended";
            }
            break;
          } else if (errMsg === "Unauthorized or Not a Member" && finalUrl.includes("/api/messages")) {
            console.warn("[OfflineSync] Removed from room! Wiping local outbox entry and messages store caches for the conversation.");
            // Delete the failed request from outbox
            await db.delete(STORE_NAME, req.id!);
            
            // Purge messages cache in IndexedDB
            const msgTx = db.transaction('messages', 'readwrite');
            const msgStore = msgTx.objectStore('messages');
            await msgStore.clear();
            await msgTx.done;
          }
        }
      }
    } catch (err) {
      console.error(`[OfflineSync] Network error while replaying ${req.url}:`, err);
      break; 
    }
  }
};
